import React, { useMemo } from 'react';
import { TextField, TextFieldProps, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { EntitySchema } from '../types';
import { useEntityContext } from '../Entity.context';

export interface EntitySchemaSelectorProps {
  value?: string;
  disabled?: boolean;
  className?: string;
  // match relation.target
  filterRelations?: string[];
  textFieldProps?: Omit<TextFieldProps, 'variant'>;
  onChange?: (schema: EntitySchema | undefined) => void;
}

const EntitySchemaSelector: React.FC<EntitySchemaSelectorProps> = props => {
  const {
    value,
    disabled,
    className,
    filterRelations,
    textFieldProps,
    onChange
  } = props;
  const { schemas, isLoading, error } = useEntityContext();

  const options = useMemo(() => {
    return schemas.filter(schema => {
      // filter relations
      const isRelated =
        !filterRelations ||
        !schema.relations ||
        schema.relations.some(relation => {
          return filterRelations.includes(relation.target);
        });
      return isRelated;
    });
  }, [schemas, filterRelations]);

  const selected = useMemo(() => {
    return schemas.find(schema => schema.name === value);
  }, [value, schemas]);

  return (
    <Autocomplete
      options={options}
      disabled={disabled}
      loading={isLoading}
      className={className}
      value={selected || null}
      getOptionLabel={option => option.displayName}
      onChange={(_, value) => onChange?.(value || undefined)}
      renderOption={option => (
        <span>
          <Typography>{option.displayName}</Typography>
          {option.displayName !== option.name && (
            <Typography variant='body2' color='textSecondary'>
              {option.name}
            </Typography>
          )}
        </span>
      )}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth
          variant='outlined'
          label='Select Entity Schema'
          error={!!error}
          helperText={error?.message}
          {...textFieldProps}
        />
      )}
    />
  );
};

export default EntitySchemaSelector;
